import React, { useState, useRef, useEffect } from 'react';
import { Upload, FileText, X } from 'lucide-react';
import EditModalWrapper from './EditModalWrapper';

const DOCUMENT_TYPES = [
    '10th',
    '12th',
    'Degree',
    'Registration',
    'Transcript',
    'CV',
    'Language Certificate',
    'Passport',
    'Mark list',
    'Birth certificate',
    'Others'
];

const AddDocumentModal = ({
    isOpen,
    onClose,
    documentData = null,
    onSave,
    isAddMode = true
}) => {
    const buildForm = (data) => ({
        fileName: data?.file_name || data?.name || '',
        documentType: data?.document_type || data?.type || '',
        issuanceDate: data?.issuance_date || data?.uploaded_on || '',
        file: null,
    });

    const [form, setForm] = useState(buildForm(documentData));
    const [selectedFile, setSelectedFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setForm(buildForm(documentData));
            setSelectedFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }, [isOpen, documentData]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            if (!form.fileName) {
                setForm({ ...form, fileName: file.name.replace(/\.[^/.]+$/, '') });
            }
        }
    };

    const removeFile = () => {
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.fileName.trim()) {
            alert('File Name is required');
            return;
        }
        if (!selectedFile && isAddMode) {
            alert('Please upload a document');
            return;
        }
        if (!form.documentType) {
            alert('Document Type is required');
            return;
        }

        setIsSubmitting(true);
        try {
            const documentEntry = {
                ...documentData,
                file_name: form.fileName.trim(),
                name: form.fileName.trim(),
                document_type: form.documentType,
                type: form.documentType,
                issuance_date: form.issuanceDate,
                uploaded_on: form.issuanceDate || new Date().toISOString().split('T')[0],
                file: selectedFile,
            };

            await onSave(documentEntry);
            onClose();
        } catch (error) {
            console.error('Error saving document:', error);
            alert('Failed to save document. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <EditModalWrapper
            isOpen={isOpen}
            onClose={onClose}
            title={isAddMode ? "Add Document" : "Edit Document"}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            submitLabel={isAddMode ? "Add" : "Update"}
            size="md"
        >
            <div className="space-y-5">
                {/* File Name */}
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        File Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={form.fileName}
                        onChange={(e) => setForm({ ...form, fileName: e.target.value })}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all bg-slate-50 text-slate-900"
                        placeholder="Enter file name"
                        required
                    />
                </div>

                {/* Upload Document */}
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        Upload Document <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        className="hidden"
                    />
                    {!selectedFile ? (
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="w-full p-6 border-2 border-dashed border-slate-300 rounded-xl hover:border-blue-400 hover:bg-blue-50/50 transition-all group"
                        >
                            <div className="flex flex-col items-center gap-2">
                                <div className="p-3 bg-blue-50 rounded-full group-hover:bg-blue-100 transition-colors">
                                    <Upload className="h-6 w-6 text-blue-500" />
                                </div>
                                <span className="text-blue-600 font-medium">Browse document</span>
                                <span className="text-xs text-slate-400">PDF, DOC, DOCX, JPG, PNG supported</span>
                            </div>
                        </button>
                    ) : (
                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-red-50 rounded-lg">
                                    <FileText className="h-5 w-5 text-red-500" />
                                </div>
                                <div>
                                    <p className="font-medium text-slate-800 text-sm">{selectedFile.name}</p>
                                    <p className="text-xs text-slate-400">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={removeFile}
                                className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-all text-slate-900"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    )}
                </div>

                {/* Document Type & Issuance Date */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            Document Type <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={form.documentType}
                            onChange={(e) => setForm({ ...form, documentType: e.target.value })}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all bg-slate-50 text-slate-900"
                            required
                        >
                            <option value="">Select document type</option>
                            {DOCUMENT_TYPES.map((type) => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            Issuance Date
                        </label>
                        <input
                            type="date"
                            value={form.issuanceDate}
                            onChange={(e) => setForm({ ...form, issuanceDate: e.target.value })}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all text-slate-900"
                        />
                    </div>
                </div>
            </div>
        </EditModalWrapper>
    );
};

export default AddDocumentModal;
